import { useEffect, useLayoutEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isAndroidApp } from "../../tools/android";
import { isIOSApp } from "../../tools/ios";
import { displayToast } from "../../components/app/AppToast";
import Loader from "../../components/Loader";
import { useSignupOnboardingState } from "../../domains/auth/hooks/useSignupOnboardingState";
import {
  clearRoomInviteCodeData,
  setRoomInviteCodeData,
} from "../../domains/auth/stores/roomInviteCodeStore";
import { apiClient, revalidateApiQuery } from "../../services/api";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import {
  ensureCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { resetSignupFirstVisitFlags } from "../../domains/user/stores/firstVisitFlagsStore";
import { OauthRoomsConfig } from "../../features/oauth";
import { useStartRoomOAuth } from "../../features/oauth/useStartRoomOAuth";
import { gaEventShortUrlConverted } from "../../tools/analytics/domains/onboarding.analytics";
import { gaEventRoomJoin } from "../../tools/analytics/domains/invite.analytics";
import AppInstall from "../app-install/AppInstall";
import { isInstallWallDisabledByQuery } from "../../domains/app/appInstallWall";
import useCallbackStable from "../../hooks/use-callback-stable";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { useTranslation } from "react-i18next";
import { ClientError } from "../../../../api/src/shared/request";
import { getRoomInvitePathname } from "../../domains/room/utils/getRoomPathname";
import {
  askForProfessionalRecord,
  needsProfessionalRecord,
} from "../../components/room/ProfessionalRecordHost";
import { openRoomWelcome } from "../../components/room/RoomWelcomeHost";

const debug = new DebugLogger("InviteLinkPage");
debug.mute();

const NATIVE_BRIDGE_WAIT_INTERVAL_MS = 50;
const NATIVE_BRIDGE_WAIT_MAX_MS = 1000;

function isLikelyAndroidNativeWebView(userAgent: string): boolean {
  return /Android/.test(userAgent) && /; wv\)/.test(userAgent);
}

export default function InviteLinkPage({ inviteCode }: { inviteCode: string }) {
  const history = useHistory();
  const startRoomOAuth = useStartRoomOAuth();
  const location = useLocation();
  // Same switch, read from the one place that owns it now.
  const appInstallDisabled = isInstallWallDisabledByQuery(location.search);
  const { t } = useTranslation();
  const isQrCode = inviteCode === "qrcode";

  // Clear localStorage synchronously before useSignupOnboardingState reads it.
  // useLayoutEffect runs before useEffect, preventing the race condition where
  // stale invite code data from a previous visit gets hydrated into state.
  useLayoutEffect(() => {
    clearRoomInviteCodeData();
  }, []);

  const { updateSignupOnboardingState } = useSignupOnboardingState();

  const userAgent = globalThis.navigator.userAgent;
  const isMobile = userAgent.includes("Mobile");
  const shouldWaitForAndroidBridge = isLikelyAndroidNativeWebView(userAgent);
  const initialAndroidBridgeReady = isAndroidApp();
  const [androidBridgeState, setAndroidBridgeState] = useState(() => ({
    resolved: !shouldWaitForAndroidBridge || initialAndroidBridgeReady,
    ready: initialAndroidBridgeReady,
  }));
  const isAndroidBridgeResolved = androidBridgeState.resolved;
  const isAndroidBridgeReady = androidBridgeState.ready;

  useEffect(() => {
    if (androidBridgeState.resolved) {
      return;
    }

    let cancelled = false;
    const resolveBridgeState = async () => {
      for (
        let elapsed = 0;
        elapsed <= NATIVE_BRIDGE_WAIT_MAX_MS;
        elapsed += NATIVE_BRIDGE_WAIT_INTERVAL_MS
      ) {
        if (cancelled) return;
        if (isAndroidApp()) {
          setAndroidBridgeState({ resolved: true, ready: true });
          return;
        }
        if (elapsed < NATIVE_BRIDGE_WAIT_MAX_MS) {
          await new Promise((resolve) =>
            setTimeout(resolve, NATIVE_BRIDGE_WAIT_INTERVAL_MS)
          );
        }
      }

      if (!cancelled) {
        setAndroidBridgeState((previousState) => {
          if (previousState.resolved) {
            return previousState;
          }
          return { resolved: true, ready: false };
        });
      }
    };

    void resolveBridgeState();
    return () => {
      cancelled = true;
    };
  }, [androidBridgeState.resolved]);

  const isNative = isAndroidBridgeReady || isIOSApp();
  const shouldShowAppInstall = isMobile && !isNative && !appInstallDisabled;

  const initialize = useCallbackStable(async () => {
    try {
      if (!inviteCode) {
        throw new Error("No Invite code provided.");
      }

      const searchParams = new URLSearchParams(location.search);
      const sharingQrCodeId = searchParams.get("qrCodeId");
      if (isQrCode && !sharingQrCodeId) {
        throw new Error("No QR Code ID provided.");
      }

      const utmSource = searchParams.get("utm_source");
      const utmMedium = searchParams.get("utm_medium");
      const utmContent = searchParams.get("utm_content");
      // Every code the dashboard and the share sheet mint lands here with
      // `utm_medium=qrcode` (the legacy QRFY codes come through `/invite/qrcode`).
      const roomDiscoveryChannel =
        isQrCode || utmMedium === "qrcode" ? "qr code" : null;
      const playlistParam = searchParams.get("playlist");
      const professionParam = searchParams.get("profession");
      const specialties = [];
      const mainSpecialtyParam = searchParams.get("main_specialty");
      const specialtiesParam = searchParams.get("specialties");
      const medicalSpecialityParam = searchParams.get("medical_speciality");
      if (mainSpecialtyParam) specialties.push(mainSpecialtyParam);
      if (specialtiesParam) specialties.push(...specialtiesParam.split(","));
      if (medicalSpecialityParam) specialties.push(medicalSpecialityParam);

      let roomInviteCode: string;
      if (isQrCode && sharingQrCodeId) {
        // For legacy QRFY QR codes, resolve the qrCodeId to an invite code first
        // Old QRFY QR Code → /invite/qrcode?qrCodeId=123
        // ↓
        // Call /webapp/public/rooms/by-legacy-qr-code/123
        // ↓
        // Returns { inviteCode: "abc123" }
        // ↓
        // Normal invite flow with "abc123"
        const { body: legacyInviteCode } = await apiClient.get(
          "/webapp/public/rooms/by-legacy-qr-code/:sharingQrCodeId",
          { param: { sharingQrCodeId: Number(sharingQrCodeId) } }
        );
        roomInviteCode = legacyInviteCode;
      } else {
        roomInviteCode = inviteCode;
      }

      const { body: roomPreview } = await apiClient.get(
        "/webapp/public/rooms/by-invite/:code",
        { param: { code: roomInviteCode } }
      );

      // A member opens the room. A join asks nothing: the room opens with its
      // welcome sheet (docs/partner-objection.md, REQ-05).
      const isAuthentified = await isUserAuthentified();
      if (isAuthentified) {
        const { body: privateRooms } = await apiClient.get(
          "/webapp/private/rooms"
        );
        const alreadyJoinedRoom = privateRooms.rooms.find(
          (r) => r.id === roomPreview.id
        );
        if (alreadyJoinedRoom) {
          const roomPath = getRoomInvitePathname(
            alreadyJoinedRoom.id,
            playlistParam
          );
          history.replace(roomPath);
          return;
        }
      }

      // Invite expired or max-uses reached — block early.
      // Only non-members reach this point (members were redirected above).
      if (roomPreview.expired) {
        displayToast(t("This invite link is no longer valid."), "error");
        history.replace("/discovery");
        return;
      }

      const roomForceProfession =
        professionParam !== null && professionParam.length > 0
          ? professionParam
          : null;
      const roomForceSpecialties =
        specialties.length > 0 ? [...new Set(specialties)] : [];

      // persist the invite code data in local storage
      // to avoid losing it if the user refreshes the page
      setRoomInviteCodeData({
        roomInviteCode,
        roomForceProfession,
        roomForceSpecialties,
        roomUtmSource: utmSource,
        roomUtmMedium: utmMedium,
        roomUtmContent: utmContent,
        roomDiscoveryChannel,
        roomPlaylistId: playlistParam,
      });

      updateSignupOnboardingState({
        roomInviteCode,
        roomForceProfession,
        roomForceSpecialties,
        roomUtmSource: utmSource,
        roomUtmMedium: utmMedium,
        roomUtmContent: utmContent,
        roomDiscoveryChannel,
        roomPlaylistId: playlistParam,
      });

      gaEventShortUrlConverted();

      if (!isAuthentified) {
        history.push("/welcome");
        return;
      }

      // The onboarding detour exists ONLY to defer the join until an incomplete
      // account is ready — `OnboardingPage.finishOnboarding` consumes the code
      // we just persisted. An account that already completed onboarding has no
      // step left to run: `advanceAfterCarousel` short-circuits straight to
      // Discovery and the invite is silently dropped. Join here instead.
      const currentUser = await ensureCurrentUser();
      if (currentUser === null || currentUser.onboardingCompleted !== true) {
        history.push("/signup/onboarding");
        return;
      }

      // A room that asks for a professional record admits nobody without one.
      // The reader saves one in the modal or does not join. It comes before the
      // partner sign-in of an OAuth room, whose callback joins the room.
      if (needsProfessionalRecord(roomPreview, currentUser)) {
        const outcome = await askForProfessionalRecord();
        if (outcome === "cancelled") {
          clearRoomInviteCodeData();
          history.replace("/discovery");
          return;
        }
      }

      // Partner-IdP-gated room: authenticate against the IdP first — its
      // callback page (`/oauth/callback/:roomId`) consumes the persisted code.
      const oauthRoom = OauthRoomsConfig.find((r) => r.id === roomPreview.id);
      if (oauthRoom !== undefined) {
        for (const room of OauthRoomsConfig) {
          room.manager.logout();
        }
        const outcome = await startRoomOAuth(oauthRoom, roomPreview.id);
        // This component renders a full-screen loader and nothing else, so a
        // reader who closes the sheet would sit on a spinner with no control.
        // Send them where the failure paths below already send them.
        if (outcome.kind === "abandoned") history.replace("/discovery");
        return;
      }

      const { body: joinedRoom } = await apiClient.post(
        "/webapp/private/rooms/join/by-invite/:code",
        {
          param: { code: roomInviteCode },
          json: {
            discoveryChannel:
              roomDiscoveryChannel === null
                ? "public app"
                : roomDiscoveryChannel,
          },
        }
      );

      // Fresh room join — reset first-visit flags so the download-app modal and
      // profile-picture prompt fire again.
      resetSignupFirstVisitFlags();

      gaEventRoomJoin({
        roomUid: joinedRoom.id,
        userUid: currentUser.uid,
        utmSource,
        utmMedium,
        utmContent,
        origin: "invite link",
        roomType: joinedRoom.isPublic ? "public" : "private",
        roomName: joinedRoom.translations.en.name,
        roomCount: currentUser.rooms.length,
        roomDiscoveryChannel:
          roomDiscoveryChannel === null ? "public app" : roomDiscoveryChannel,
      });

      // Both AWAITED now that the destination is the room itself: its pages
      // derive `hasJoinedRoom` from the rooms list and read `currentUser`,
      // so navigating on the pre-join caches paints a "Join this
      // room" CTA at the member we just welcomed.
      await revalidateApiQuery("/webapp/private/rooms");
      await reloadCurrentUser();
      clearRoomInviteCodeData();
      const roomPath = getRoomInvitePathname(joinedRoom.id, playlistParam);

      history.replace(roomPath);
      openRoomWelcome(
        joinedRoom.id,
        roomDiscoveryChannel === null ? "public app" : roomDiscoveryChannel
      );
    } catch (error) {
      if (
        error instanceof ClientError &&
        (error.responseStatus === 404 || error.responseStatus === 400)
      ) {
        // Expected: invite doesn't exist, expired, or max uses reached.
        // Don't console.error — avoids Sentry capture via captureConsoleIntegration.
        displayToast(error.message, "error");
      } else {
        displayToast(t("Something went wrong. Please try again."), "error");
      }
      history.replace("/discovery");
    }
  });

  useEffect(() => {
    if (!isAndroidBridgeResolved) {
      return;
    }

    // Don't consume the invite code on mobile web
    // instead display the app install screen
    if (shouldShowAppInstall) {
      return;
    }

    initialize();
  }, [isAndroidBridgeResolved, shouldShowAppInstall, initialize]);

  if (shouldShowAppInstall) {
    return <AppInstall escape={{ kind: "none" }} />;
  }

  if (!isAndroidBridgeResolved) {
    return <Loader expanded />;
  }

  return <Loader expanded />;
}
